import { useMemo } from "react";
import dayjs from "dayjs";

/* =========================
   VIEWERSHIP CALCULATION
========================= */

function generateNumberFromTime() {
  const outputValues = [];
  for (let minutesSinceMidnight = 0; minutesSinceMidnight < 1440; minutesSinceMidnight++) {
    const phaseAngle = (2 * Math.PI * minutesSinceMidnight) / 1440;
    const outputValue = 3.14 + Math.cos((phaseAngle + Math.PI / 2)) * 0.5; 
    outputValues.push(outputValue);
  }
  return outputValues;
}

const viewerNumbers = generateNumberFromTime();

/* =========================
   HELPERS
========================= */

function uiDateToDataDay(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  const dt = dayjs(`${d}-${m}-${y}`, "DD-MM-YYYY", true);
  return dt.isValid() ? dt : null;
}

function getWeekStartMonday(d) {
  const offset = (d.day() + 6) % 7;
  return d.subtract(offset, "day").startOf("day");
}

function minuteToLabel(i) {
  const h = String(Math.floor(i / 60)).padStart(2, "0");
  const m = String(i % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function timeToMinute(t) {
  if (!t || typeof t !== "string") return null;
  const [h, m] = t.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function applyViewershipMultiplier(rawVideo) {
  if (!rawVideo) return {};
  
  const tempVideoConsumptionViewer = {};
  
  Object.entries(rawVideo).forEach(([tempDate, consumption]) => {
    tempVideoConsumptionViewer[tempDate] = consumption.map((value, j) => {
      if (j < viewerNumbers.length) {
        return value * viewerNumbers[j];
      }
      return value;
    });
  });
  
  return tempVideoConsumptionViewer;
}

/* =========================
   MAIN HOOK
========================= */

export function useAvgActiveViewerSeries({
  rawVideo,
  sortedVideoKeys,
  startDateInput,
  endDateInput,
  startTime,
  endTime,
  mode,
}) {
  return useMemo(() => {
    if (!rawVideo || !sortedVideoKeys?.length) return [];

    // Apply viewership multiplier to all data
    const viewerVideo = applyViewershipMultiplier(rawVideo);

    /* =========================
       MINUTE  ✅ (TIME PICKER MODE)
    ========================= */

    if (mode === "minute") {
      const uiKey = startDateInput
        ? startDateInput.split("-").reverse().join("-")
        : sortedVideoKeys.at(-1);

      const key = viewerVideo[uiKey] ? uiKey : sortedVideoKeys.at(-1);
      const arr = viewerVideo[key] || [];

      let startMin = timeToMinute(startTime);
      let endMin = timeToMinute(endTime);

      if (!Number.isFinite(startMin)) startMin = 0;
      if (!Number.isFinite(endMin)) endMin = 1439;

      // ✅ clamp
      startMin = Math.max(0, Math.min(1439, startMin));
      endMin = Math.max(0, Math.min(1439, endMin));

      // ✅ if user selected reversed range, auto-fix
      if (endMin < startMin) {
        const tmp = startMin;
        startMin = endMin;
        endMin = tmp;
      }

      const out = [];
      for (let i = startMin; i <= endMin; i++) {
        out.push({
          index: i,
          label: minuteToLabel(i),
          value: Math.round((Number(arr[i]) || 0) / 2),
        });
      }

      return out;
    }

    /* =========================
       DATE RANGE
    ========================= */

    const start = uiDateToDataDay(startDateInput);
    const end = uiDateToDataDay(endDateInput);
    if (!start || !end) return [];

    /* =========================
       HOUR
    ========================= */

    if (mode === "hour") {
      const out = [];
      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dataKey = current.format("DD-MM-YYYY");
        const arr = viewerVideo[dataKey];

        if (arr) {
          for (let h = 0; h < 24; h++) {
            const hourArr = arr.slice(h * 60, (h + 1) * 60);
            const sum = hourArr.reduce((acc, v) => acc + (Number(v) || 0), 0);
            const avg = sum / 60;

            out.push({
              label: current.format("DD/MM") + ` ${String(h).padStart(2, "0")}:00`,
              value: Math.round(avg / 2),
            });
          }
        }
        current = current.add(1, "day");
      }

      return out;
    }

    /* =========================
       DAY
    ========================= */

    if (mode === "day") {
      const out = [];
      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dataKey = current.format("DD-MM-YYYY");
        const arr = viewerVideo[dataKey];

        if (arr) {
          const sum = arr.reduce((acc, v) => acc + (Number(v) || 0), 0);
          const avg = sum / 1440;

          out.push({
            label: current.format("DD/MM/YYYY"),
            value: Math.round(avg / 2),
          });
        }

        current = current.add(1, "day");
      }

      return out;
    }

    /* =========================
       WEEK
    ========================= */

    if (mode === "week") {
      const weeks = new Map();
      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dataKey = current.format("DD-MM-YYYY");
        const arr = viewerVideo[dataKey];

        if (arr) {
          const weekStart = getWeekStartMonday(current);
          const wKey = weekStart.format("YYYY-MM-DD");

          if (!weeks.has(wKey)) weeks.set(wKey, { weekStart, minuteData: [] });
          weeks.get(wKey).minuteData.push(...arr);
        }

        current = current.add(1, "day");
      }

      const out = [];
      for (const { weekStart, minuteData } of weeks.values()) {
        const sum = minuteData.reduce((acc, v) => acc + (Number(v) || 0), 0);
        const dayCount = Math.ceil(minuteData.length / 1440);
        const avg = sum / (dayCount * 1440);

        out.push({
          label: weekStart.format("DD.MM"),
          value: Math.round(avg / 2),
        });
      }

      return out;
    }

    /* =========================
       MONTH
    ========================= */

    if (mode === "month") {
      const months = new Map();
      let current = start;

      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dataKey = current.format("DD-MM-YYYY");
        const arr = viewerVideo[dataKey];

        if (arr) {
          const mKey = current.format("YYYY-MM");
          if (!months.has(mKey)) months.set(mKey, { month: current.clone(), minuteData: [] });
          months.get(mKey).minuteData.push(...arr);
        }

        current = current.add(1, "day");
      }

      const out = [];
      for (const { month, minuteData } of months.values()) {
        const sum = minuteData.reduce((acc, v) => acc + (Number(v) || 0), 0);
        const dayCount = Math.ceil(minuteData.length / 1440);
        const avg = sum / (dayCount * 1440);

        out.push({
          label: month.format("MM.YYYY"),
          value: Math.round(avg / 2),
        });
      }

      return out;
    }

    return [];
  }, [rawVideo, sortedVideoKeys, startDateInput, endDateInput, startTime, endTime, mode]);
}
